import { useQuery } from '@tanstack/react-query';
import {
  getAllCategoryByCategoryTypes,
  getAllCategoryTypes,
  Category,
  CategoryByCategoryType,
  CategoryType,
  getAllProductByCategoryType
} from 'api/categories/_requests';
import EcomWhopingBanner from 'components/banners/EcomWhopingBanner';
import Section from 'components/base/Section';
import CategoryTypeItem from 'components/common/CategoryTypeItem';
import CategoryTypeWiseCatItem from 'components/common/CategoryTypeWiseCatItem';
import EcomCategoriesWisePorduct from 'components/sliders/EcomCategoriesWisePorduct';
import { ProductCat } from 'data/e-commerce/products';
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Container, Row, Spinner } from 'react-bootstrap';
import { useParams } from 'react-router-dom';

const CategoryTypePage = () => {
  const { slug } = useParams();
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null
  );
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  const { data: categoryTypes, isLoading: isCategoryTypesLoading } = useQuery<
    CategoryType[]
  >({
    queryKey: ['all-categoryType'],
    queryFn: getAllCategoryTypes
  });

  const { data: categoryTypesCategory, isLoading: isCategoriesLoading } =
    useQuery<CategoryByCategoryType>({
      queryKey: ['categories-by-type', slug],
      queryFn: () => getAllCategoryByCategoryTypes(slug),
      enabled: !!slug
    });

  const { data: productsByCategoryType, isLoading: isProductsTypeLoading } =
    useQuery<ProductCat[]>({
      queryKey: ['products-by-categoryType', slug],
      queryFn: () => getAllProductByCategoryType(slug || ''),
      enabled: !!slug
    });

  useEffect(() => {
    setSelectedCategory(null);
    setCurrentPage(1);
  }, [slug]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory]);

  const categoryTypeName = useMemo(() => {
    return categoryTypes?.find(ct => ct.slug === slug)?.name || '';
  }, [categoryTypes, slug]);

  const filteredProducts = useMemo(() => {
    if (!productsByCategoryType) return [];
    return selectedCategory
      ? productsByCategoryType.filter(p => p.category === selectedCategory._id)
      : productsByCategoryType;
  }, [productsByCategoryType, selectedCategory]);

  const productCount = filteredProducts.length;

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage]);

  const renderLoader = (text: string) => (
    <div className="text-center py-5">
      <Spinner animation="border" role="status" />
      <p className="mt-3">{text}</p>
    </div>
  );

  return (
    <div className="pt-5 mb-9">
      {/* <Section small className="py-0"> */}
      <Container className="px-xl-0 px-xxl-3">
        <Row className="g-3">
          <Col xs={12}>
            <div className="card">
              <div className="card-body p-0 mx-5 shadow-sm">
                {isCategoryTypesLoading ? (
                  renderLoader('Loading category types...')
                ) : (
                  <CategoryTypeItem
                    typename={slug || ''}
                    categoryType={categoryTypes || []}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>

        <Row className="g-3 mb-3 mt-3">
          <Col xs={12}>
            <EcomWhopingBanner />
          </Col>
        </Row>

        <Row className="g-3">
          <Col xs={12}>
            <div className="card">
              <h4 className="card-header p-0 mx-5 py-3">
                All Categories{' '}
                <span className="text-primary">
                  ({categoryTypesCategory?.total ?? 0})
                </span>
              </h4>
              <div className="card-body p-0 mx-5 shadow-sm">
                {isCategoriesLoading ? (
                  renderLoader('Loading categories...')
                ) : (
                  <CategoryTypeWiseCatItem
                    total={categoryTypesCategory?.total ?? 0}
                    categoryTypesCategory={
                      categoryTypesCategory?.categories || []
                    }
                    onCategoryClick={cat => setSelectedCategory(cat)}
                    selectedCatId={selectedCategory?._id || ''}
                  />
                )}
              </div>
            </div>
          </Col>
        </Row>

        <div className="mb-6 mt-3">
          {isProductsTypeLoading ? (
            renderLoader('Loading products...')
          ) : (
            <>
              <EcomCategoriesWisePorduct
                counts={productCount}
                category={selectedCategory?.name || categoryTypeName}
                products={paginatedProducts}
              />

              {/* Pagination */}
              {productCount > itemsPerPage && (
                <div className="d-flex justify-content-center mt-4">
                  <ul className="pagination">
                    {Array.from({
                      length: Math.ceil(productCount / itemsPerPage)
                    }).map((_, index) => (
                      <li
                        key={index}
                        className={`page-item ${
                          currentPage === index + 1 ? 'active' : ''
                        }`}
                        onClick={() => setCurrentPage(index + 1)}
                        style={{ cursor: 'pointer' }}
                      >
                        <span className="page-link">{index + 1}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
        </div>
      </Container>
      {/* </Section> */}
    </div>
  );
};

export default CategoryTypePage;
